import React, { useState, useEffect } from 'react';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { getGlyphIconDataUri } from '../../../utils/avatarIcons';
import type { SphereAvatarDialProps } from './types';
import './SphereAvatarDial.scss';

/**
 * Dial component that displays the sphere's avatar in the center.
 * Shows image if available, otherwise falls back to sphere type icon.
 * Always displays sphere name below.
 */
const SphereAvatarDial: React.FC<SphereAvatarDialProps> = ({
  sphere,
  onClick,
}) => {
  const apiClients = useApiClientsOrNull();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Reset image when sphere changes to prevent stale images
  useEffect(() => {
    setImageUrl('');
  }, [sphere?.id]);

  useEffect(() => {
    let isMounted = true;

    const fetchImage = async () => {
      if (!apiClients?.graphClient || !sphere?.id || !sphere?.properties?.imageId) {
        return;
      }

      setLoading(true);

      try {
        // Use the global sphere image cache for intelligent batching
        const cachedData = await globalSphereCache.getSphereData(
          sphere.id,
          apiClients.graphClient
        );

        if (isMounted) {
          // Use full resolution imageUrl instead of thumbnailUrl
          setImageUrl(cachedData.imageUrl || cachedData.thumbnailUrl || '');
        }
      } catch (error) {
        console.debug('[SphereAvatarDial] Avatar image load failed:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Only fetch if we have required data
    const imageId = sphere?.properties?.imageId;
    if (
      sphere?.id &&
      imageId &&
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(imageId)
    ) {
      fetchImage();
    }

    return () => {
      isMounted = false;
    };
  }, [sphere?.id, sphere?.properties?.imageId, apiClients?.graphClient]);

  if (!sphere) {
    return (
      <div className="sphere-avatar-dial sphere-avatar-dial--empty">
        <div className="sphere-avatar-dial__placeholder" />
      </div>
    );
  }

  const sphereName = sphere.properties?.name || 'Sphere';
  const iconDataUri = getGlyphIconDataUri(sphere.glyph, sphere.glyphColor);

  return (
    <button
      type="button"
      className="sphere-avatar-dial"
      onClick={onClick}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={sphereName}
          className="sphere-avatar-dial__image"
        />
      ) : (
        <div className="sphere-avatar-dial__icon">
          {!loading && (
            <img
              src={iconDataUri}
              alt={`${sphereName} icon`}
              className="sphere-avatar-dial__icon-img"
            />
          )}
        </div>
      )}
    </button>
  );
};

export default SphereAvatarDial;
